import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Briefcase, Users, Calendar, DollarSign, Eye, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useTranslation } from "@/hooks/useTranslation";

// Mock data - in real app this would come from API
const mockPlans = [
  {
    id: "1",
    title: "Q1 2024 Engineering Expansion",
    status: "ongoing",
    goal: "Hire 5 senior engineers for product development",
    budget: 75000,
    positions: 5,
    deadline: "2024-03-31",
    needsRecruiter: true,
    jobsPosted: 3,
    candidatesReceived: 24,
    department: "Engineering",
    priority: "high",
  },
  {
    id: "2",
    title: "Marketing Team Build-out",
    status: "propose",
    goal: "Establish marketing department with 3 specialists",
    budget: 45000,
    positions: 3,
    deadline: "2024-04-15",
    needsRecruiter: false,
    jobsPosted: 0,
    candidatesReceived: 0,
    department: "Marketing",
    priority: "medium",
  },
  {
    id: "3",
    title: "Summer Internship Program",
    status: "paused",
    goal: "Onboard 8 interns across different departments",
    budget: 20000,
    positions: 8,
    deadline: "2024-06-01",
    needsRecruiter: false,
    jobsPosted: 2,
    candidatesReceived: 56,
    department: "All",
    priority: "low",
  },
  {
    id: "4",
    title: "2023 Sales Team Expansion",
    status: "past",
    goal: "Completed hiring of 4 sales representatives",
    budget: 60000,
    positions: 4,
    deadline: "2023-12-31",
    needsRecruiter: true,
    jobsPosted: 4,
    candidatesReceived: 89,
    department: "Sales",
    priority: "high",
  },
];

const mockJobs = [
  {
    id: "1",
    planId: "1",
    title: "Senior Frontend Engineer",
    type: "Full-time",
    location: "Remote",
    salary: "$120,000 - $160,000",
    datePosted: "2024-01-15",
    status: "active",
    candidatesCount: 12,
    candidates: [
      { id: "1", name: "Alice Johnson", email: "alice@example.com", status: "reviewing", appliedDate: "2024-01-18", experience: "5 years" },
      { id: "2", name: "Bob Smith", email: "bob@example.com", status: "interviewed", appliedDate: "2024-01-20", experience: "7 years" },
      { id: "3", name: "Carol Davis", email: "carol@example.com", status: "rejected", appliedDate: "2024-01-16", experience: "3 years" },
    ]
  },
  {
    id: "2",
    planId: "1",
    title: "Senior Backend Engineer",
    type: "Full-time",
    location: "New York, NY",
    salary: "$130,000 - $170,000",
    datePosted: "2024-01-20",
    status: "active",
    candidatesCount: 8,
    candidates: [
      { id: "4", name: "David Wilson", email: "david@example.com", status: "reviewing", appliedDate: "2024-01-22", experience: "6 years" },
      { id: "5", name: "Eva Brown", email: "eva@example.com", status: "interviewed", appliedDate: "2024-01-24", experience: "8 years" },
    ]
  },
  {
    id: "3",
    planId: "1",
    title: "DevOps Engineer",
    type: "Full-time",
    location: "San Francisco, CA",
    salary: "$125,000 - $165,000",
    datePosted: "2024-01-25",
    status: "active",
    candidatesCount: 4,
    candidates: [
      { id: "6", name: "Frank Miller", email: "frank@example.com", status: "reviewing", appliedDate: "2024-01-26", experience: "4 years" },
    ]
  },
];

export function PlanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('ui');
  const [expandedJobs, setExpandedJobs] = useState<string[]>([]);
  
  const plan = mockPlans.find(p => p.id === id);
  const planJobs = mockJobs.filter(job => job.planId === id);
  
  if (!plan) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Plan Not Found</h2>
          <p className="text-muted-foreground mb-4">The hiring plan you're looking for doesn't exist.</p>
          <Button onClick={() => navigate("/workspace/hiring-center")}>Back to Hiring Center</Button>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "propose":
        return "bg-blue-500/10 text-blue-600 border-blue-200";
      case "ongoing":
        return "bg-green-500/10 text-green-600 border-green-200";
      case "paused":
        return "bg-yellow-500/10 text-yellow-600 border-yellow-200";
      case "past":
        return "bg-gray-500/10 text-gray-600 border-gray-200";
      default:
        return "bg-gray-500/10 text-gray-600 border-gray-200";
    }
  };

  const getCandidateStatusColor = (status: string) => {
    switch (status) {
      case "reviewing":
        return "bg-blue-500/10 text-blue-600";
      case "interviewed":
        return "bg-green-500/10 text-green-600";
      case "rejected":
        return "bg-red-500/10 text-red-600";
      case "hired":
        return "bg-purple-500/10 text-purple-600";
      default:
        return "bg-gray-500/10 text-gray-600";
    }
  };

  const toggleJobExpansion = (jobId: string) => {
    setExpandedJobs(prev => 
      prev.includes(jobId) 
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate("/workspace/hiring-center")}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Hiring Center
          </Button>
        </div>

        {/* Top Information Section */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-xl md:text-2xl font-semibold tracking-tight mb-2">{plan.title}</CardTitle>
                <p className="text-muted-foreground mb-4">{plan.goal}</p>
                <div className="flex items-center gap-3">
                  <Badge className={getStatusColor(plan.status)}>
                    {t(`status.${plan.status}`)}
                  </Badge>
                  <Badge variant="outline">{plan.department}</Badge>
                  <Badge variant="outline" className={
                    plan.priority === "high" ? "border-red-200 text-red-600" :
                    plan.priority === "medium" ? "border-yellow-200 text-yellow-600" :
                    "border-green-200 text-green-600"
                  }>
                    {plan.priority.charAt(0).toUpperCase() + plan.priority.slice(1)} Priority
                  </Badge>
                </div>
              </div>
              <Button variant="outline" className="gap-2">
                <Eye className="w-4 h-4" />
                Detail View
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-3">
                <DollarSign className="w-8 h-8 text-green-600 bg-green-100 rounded-lg p-2" />
                <div>
                  <p className="text-sm text-muted-foreground">Total Budget</p>
                  <p className="text-lg font-semibold">${plan.budget.toLocaleString()}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-blue-600 bg-blue-100 rounded-lg p-2" />
                <div>
                  <p className="text-sm text-muted-foreground">Positions</p>
                  <p className="text-lg font-semibold">{plan.positions}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Briefcase className="w-8 h-8 text-purple-600 bg-purple-100 rounded-lg p-2" />
                <div>
                  <p className="text-sm text-muted-foreground">Jobs Posted</p>
                  <p className="text-lg font-semibold">{plan.jobsPosted}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Calendar className="w-8 h-8 text-orange-600 bg-orange-100 rounded-lg p-2" />
                <div>
                  <p className="text-sm text-muted-foreground">Deadline</p>
                  <p className="text-lg font-semibold">{new Date(plan.deadline).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Jobs Table Section */}
        <Card>
          <CardHeader>
            <CardTitle>Job Listings ({planJobs.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {planJobs.map((job) => (
                <Collapsible key={job.id} open={expandedJobs.includes(job.id)} onOpenChange={() => toggleJobExpansion(job.id)}>
                  <CollapsibleTrigger asChild>
                    <div className="border rounded-lg p-4 cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {expandedJobs.includes(job.id) ? (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          )}
                          <div>
                            <h3 className="font-semibold">{job.title}</h3>
                            <p className="text-sm text-muted-foreground">{job.type} • {job.location}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="text-sm font-medium">{job.salary}</p>
                            <p className="text-sm text-muted-foreground">Salary Range</p>
                          </div>
                          <div className="text-right">
                            <Badge variant="secondary">{job.candidatesCount} candidates</Badge>
                          </div>
                          <div className="text-right">
                            <p className="text-sm">{new Date(job.datePosted).toLocaleDateString()}</p>
                            <p className="text-sm text-muted-foreground">Posted</p>
                          </div>
                          <Badge variant="outline" className="text-green-600 border-green-200">
                            {job.status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="border-l border-r border-b rounded-b-lg p-4 bg-muted/30">
                      <h4 className="font-medium mb-4">Submitted Candidates ({job.candidates.length})</h4>
                      <div className="space-y-3">
                        {job.candidates.map((candidate) => (
                          <div key={candidate.id} className="flex items-center justify-between p-3 bg-background rounded-lg border">
                            <div className="flex items-center gap-4">
                              <div>
                                <p className="font-medium">{candidate.name}</p>
                                <p className="text-sm text-muted-foreground">{candidate.email}</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium">{candidate.experience}</p>
                                <p className="text-sm text-muted-foreground">Experience</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-sm text-muted-foreground">Applied</p>
                                <p className="text-sm">{new Date(candidate.appliedDate).toLocaleDateString()}</p>
                              </div>
                              <Badge className={getCandidateStatusColor(candidate.status)}>
                                {candidate.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
            {planJobs.length === 0 && (
              <div className="text-center py-8">
                <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Job Listings</h3>
                <p className="text-muted-foreground">
                  No jobs have been posted for this hiring plan yet.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}